import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { OrdersFormViewModel, Visibility } from '@ail/orders-api'
import { downloadFile } from '../../helpers/FileDownload'
import { useGetTenantsQuery } from '../../redux/api/customerApi'
import {
  useDeleteOrdersFormMutation,
  useGetOrderFormsStatisticsQuery,
  useGetOrdersFormsQuery,
} from '../../redux/api/ordersApi'
import Confirm from '../common/Confirm/Confirm'
import { Grid, GridColumn } from '../common/Grid/Grid'
import './RequestsAdminPanelFunctional.scss'
import { Loader } from '../common/Loader/Loader'

interface GridRow extends OrdersFormViewModel {
  tenantName: string
  isParent: boolean
  isValid: boolean
}
export const RequestsAdminPanelFunctional = () => {
  const { t, i18n } = useTranslation('common')
  const language = i18n.language

  const { data: orderForms } = useGetOrdersFormsQuery()
  const { data: tenants } = useGetTenantsQuery()
  const { data: orderFormsStats } = useGetOrderFormsStatisticsQuery()
  const [deleteOrdersForm] = useDeleteOrdersFormMutation()
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [removeRequestId, setRemoveRequestId] = useState('')
  const [removeRequestName, setRemoveRequestName] = useState('')

  const parentsMap = useMemo(() => {
    const map = new Set<string>()
    orderForms?.forEach((f) => {
      if (f.parent) {
        map.add(f.parent)
      }
    })
    return map
  }, [orderForms])

  const getTenantName = useCallback(
    (id: string) => {
      return tenants?.find((t) => t.id === id)?.name ?? ''
    },
    [tenants]
  )

  const downloadJson = useCallback(
    (form: OrdersFormViewModel) => {
      const type = 'application/json'
      const file = new Blob([JSON.stringify(form)], { type })
      const fileURL = URL.createObjectURL(file)
      const filename = `${form.tr?.[language] || form.name}.json`
      downloadFile({ fileURL, filename })
    },
    [language]
  )

  const getParentFormName = useCallback(
    (parentId: string) => {
      const parentForm = orderForms?.find((f) => f.id === parentId)
      return parentForm ? parentForm.tr?.[language] || parentForm.name : ''
    },
    [language, orderForms]
  )

  const isOrderValid = useCallback((order: OrdersFormViewModel): boolean => {
    const now = Date.now()
    const from = order.validFrom ? new Date(order.validFrom).getTime() : 0
    const to = order.validTo ? new Date(order.validTo).getTime() : Infinity
    if (isNaN(from) || isNaN(to)) {
      return false
    }
    return from <= now && now <= to
  }, [])

  //! check whether delta selector does not break on clearing fitlers with new changes
  const filterFns = useMemo(() => {
    return {
      byGlobalString: (form: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true

        const targetString = [
          form.owner,
          form.tenantName,
          form.tr?.[language],
          form.name,
          form.id,
          form.visibility,
          ...(form.approvalProcessConfigs?.map((wf) => wf.name) || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return splitQuery.every((q) => targetString.includes(q))
      },
      byFormId: (form: GridRow, value: string) => {
        const val = value.trim().toLowerCase()
        return form.id.includes(val)
      },
      byName: (form: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true

        const fullName = `${form.name || ''} ${form.tr?.[language] || ''}`.toLowerCase()

        return splitQuery.every((q) => fullName.includes(q))
      },
      byVisibility: (row: GridRow, value?: Visibility | string) => {
        if (!value) return true
        return row.visibility ? row.visibility === value : value === 'Not set' ? true : false
      },
      byOwner: (row: GridRow, value?: string) => {
        return row.tenantName === value || !value
      },
      byStatus: (row: GridRow, value?: string) => {
        if (value) {
          return value === 'Valid' ? row.isValid : !row.isValid
        } else return true
      },
    }
  }, [language])
  const dataRows: GridRow[] = useMemo(() => {
    return (
      orderForms?.map((f) => {
        return {
          ...f,
          tenantName: getTenantName(f.ownerTenant),
          isParent: parentsMap.has(f.id),
          isValid: isOrderValid(f),
        }
      }) ?? []
    )
  }, [orderForms, getTenantName, parentsMap, isOrderValid])
  const basedOnParent = useCallback(
    (parentId: string | undefined) => {
      return !!parentId && !!orderForms?.find((f) => f.id === parentId)
    },
    [orderForms]
  )

  const selectorOptionsList = useMemo(() => {
    const uniqueOwners = new Set<string>()

    dataRows.forEach((row) => {
      if (row.tenantName) {
        uniqueOwners.add(row.tenantName)
      }
    })

    return {
      owners: [...uniqueOwners].sort(),
    }
  }, [dataRows])
  // #region Columns
  const columns = useMemo(() => {
    const cols: GridColumn<GridRow>[] = [
      {
        key: 'id',
        name: 'Form ID',
        width: 290,
        cellClass: 'id',
        renderCell: ({ row }) => <div>{row.id}</div>,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byFormId,
          customPlaceholder: 'Search by form ID',
        },
      },
      {
        key: 'parent',
        name: t('PARENT'),
        width: 210,
        cellClass: 'parent',
        renderCell: ({ row }) =>
          row.parent ? (
            <NavLink to={`/RequestsManagement/EditForm/${row.parent}`}>{getParentFormName(row.parent)}</NavLink>
          ) : null,
      },
      {
        key: 'name',
        name: 'Form name',
        width: 400,
        cellClass: 'type',
        renderCell: ({ row }) => (
          <div>
            {row.name}
            {row.tr && row.tr[language] && ` | ${row.tr[language]}`}
          </div>
        ),
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byName,
          customPlaceholder: 'Search by name',
        },
        sortBy: (row) => `${row.name}${row.tr && row.tr[language] ? ` | ${row.tr[language]}` : ''}`,
      },
      {
        key: 'owner',
        name: 'Owners',
        width: 130,
        cellClass: 'owner',
        renderCell: ({ row }) => <div>{row.tenantName}</div>,
        filter: {
          type: 'inline_selector_filter',
          data: selectorOptionsList.owners,
          filterFn: filterFns.byOwner,
          customPlaceholder: 'Search by owner',
          enableAllOption: true,
        },
        sortBy: (row) => row.tenantName,
      },
      {
        key: 'workflows',
        name: 'Workflows',
        cellClass: 'workflow',
        renderCell: ({ row }) => <div>{row.approvalProcessConfigs?.map((wf) => wf.name).join(', ')}</div>,
      },
      {
        key: 'amount',
        name: 'Amount of requests',
        width: 110,
        cellClass: 'amount',
        filter: {
          position: 'header-right',
          type: 'inline_text_filter',
          filterFn: filterFns.byGlobalString,
          customPlaceholder: 'Global search',
        },
        renderCell: ({ row }) => {
          const amount = orderFormsStats?.[row.id] ?? 0
          return <div>{amount}</div>
        },
      },
      {
        key: 'status',
        name: t('STATUS'),
        width: 80,
        cellClass: 'status',
        filter: {
          type: 'inline_selector_filter',
          data: ['Valid', 'Invalid'],
          filterFn: filterFns.byStatus,
          enableAllOption: true,
        },
        renderCell: ({ row }) => {
          const text = row.isValid ? 'Valid' : 'Invalid'
          const statusClass = row.isValid ? 'success' : 'failed'
          return <div className={statusClass}>{text}</div>
        },
        sortBy: (row) => (row.isValid ? 1 : 0),
      },
      {
        key: 'visibility',
        name: 'Visibility',
        width: 80,
        cellClass: 'visibility',
        filter: {
          type: 'inline_selector_filter',
          data: [...Object.values(Visibility), 'Not set'].sort(),
          filterFn: filterFns.byVisibility,
          customPlaceholder: 'Search',
          enableAllOption: true,
        },
        renderCell: ({ row }) => <div>{row.visibility}</div>,
        sortBy: (row) => row.visibility,
      },
      {
        key: 'actions',
        name: t('ACTIONS'),
        width: 105,
        renderCell: ({ row }) => (
          <div className="actions">
            <a
              onClick={() => {
                setRemoveRequestId(row.id)
                setRemoveRequestName(row.name)
                setShowRemoveConfirm(true)
              }}
            >
              <i className="icon-clear" />
            </a>
            <NavLink to={`/RequestsManagement/EditForm/${row.id}`}>
              <i className="icon-edit" />
            </NavLink>
            <a onClick={() => downloadJson(row)}>
              <i className="icon-download" />
            </a>
          </div>
        ),
      },
    ]
    return cols
  }, [
    t,
    filterFns.byFormId,
    filterFns.byName,
    filterFns.byOwner,
    filterFns.byGlobalString,
    filterFns.byStatus,
    filterFns.byVisibility,
    selectorOptionsList.owners,
    getParentFormName,
    language,
    downloadJson,
    orderFormsStats,
  ])
  // #endregion

  const initialLoading = !orderForms || !tenants || !orderFormsStats

  return (
    <Loader className="grid-size" blocking={initialLoading}>
      <Grid
        id="RequestAdminPanel"
        key="RequestAdminPanel"
        title="Requests Management Panel"
        icon="icon-requests"
        columns={columns}
        rows={dataRows ?? []}
        rowKeyGetter={(item) => item.id}
        rowClass={(item) => {
          return `${basedOnParent(item.parent) ? 'based-on-parent' : ''} ${item.isParent ? 'is-parent' : ''}`
        }}
        customClass="adminPanelGrid requestsAdmin"
        renderCustomOption={() => [
          <NavLink key={'newForm'} className={'grid-options-widget'} to="/RequestsManagement/EditForm/new">
            New Form
          </NavLink>,
          <NavLink
            key={'supplierWorkflowList'}
            className={'grid-options-widget'}
            to="/RequestsManagement/SupplierWorkflows"
          >
            Supplier Workflows List
          </NavLink>,
          <NavLink key={'workflowList'} className={'grid-options-widget'} to="/RequestsManagement/Workflows">
            Workflows List
          </NavLink>,
          <NavLink key={'groupFuelCards'} className={'grid-options-widget'} to="/RequestsManagement/GroupFuelCardSets">
            Group Fuel card sets
          </NavLink>,
        ]}
        hideReorder
        hideExport
      />
      <Confirm
        visible={showRemoveConfirm}
        onClose={(confirm) => {
          if (confirm) {
            deleteOrdersForm(removeRequestId)
              .unwrap()
              .then(() => {})
              .catch(() => {})
          }
          setShowRemoveConfirm(false)
        }}
        title={t('PLEASE_CONFIRM')}
        message={`Are you sure to delete "${removeRequestName}" form?`}
      />
    </Loader>
  )
}
